import React, { Suspense } from "react";
import { connect } from "react-redux";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { Layout, Spin, Grid } from "antd";
import SideNav from "components/layout-components/SideNav";
import TopNav from "components/layout-components/TopNav";
import Loading from "components/shared-components/Loading";
import PageHeader from "components/layout-components/PageHeader";
import navigationConfig from "configs/AgencyNavigationConfig";
import AgencyViews from "views/agency-views";
import utils from "utils";
import {
  NAV_TYPE_SIDE,
  NAV_TYPE_TOP,
  SIDE_NAV_WIDTH,
  SIDE_NAV_COLLAPSED_WIDTH,
} from "constants/ThemeConstant";
import useIsFeatureClosed from "hooks/useIsFeatureClosed";
import {
  CHARTING_NAVIGATION_PATH_AGENCY_VIEW,
  FEATURE_LIST,
  QUESTIONNAIRES_NAVIGATION_PATH_AGENCY_VIEW,
} from "constants/FeatureControlContstants";
import { onMobileNavToggle, toggleCollapsedNav } from "redux/actions";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import MobileNav from "components/layout-components/MobileNav";

const AgencyLayout = ({
  navCollapsed,
  navType,
  location,
  mobileNav,
  toggleCollapsedNav,
  onMobileNavToggle,
}) => {
  const { status } = useThemeSwitcher();
  const { useBreakpoint } = Grid;

  const isChartingFeatureClosed = useIsFeatureClosed(FEATURE_LIST.CHARTING);
  const isQuestionnairesFeatureClosed = useIsFeatureClosed(
    FEATURE_LIST.QUESTIONNAIRES
  );

  if (isChartingFeatureClosed) {
    const chartingMenuIndex = navigationConfig.findIndex(
      (item) => item.path === CHARTING_NAVIGATION_PATH_AGENCY_VIEW
    );
    if (chartingMenuIndex !== -1) {
      navigationConfig.splice(chartingMenuIndex, 1);
    }
  }
  if (isQuestionnairesFeatureClosed) {
    const questionnairesMenuIndex = navigationConfig.findIndex(
      (item) => item.path === QUESTIONNAIRES_NAVIGATION_PATH_AGENCY_VIEW
    );
    if (questionnairesMenuIndex !== -1) {
      navigationConfig.splice(questionnairesMenuIndex, 1);
    }
  }
  const screens = utils.getBreakPoint(useBreakpoint());
  const isMobile = !screens.includes("lg");
  const isNavSide = navType === NAV_TYPE_SIDE;
  const isNavTop = navType === NAV_TYPE_TOP;

  const getLayoutGutter = () => {
    if (isNavTop || isMobile) {
      return 0;
    }
    return navCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH;
  };

  const currentRouteInfo = utils.getRouteInfo(
    navigationConfig,
    location.pathname
  );

  const onToggle = () => {
    if (!isMobile) {
      toggleCollapsedNav(!navCollapsed);
    } else {
      onMobileNavToggle(!mobileNav);
    }
  };

  if (status === "loading") {
    return <Loading cover="page" />;
  }

  return (
    <Suspense fallback={<Spin />}>
      <Layout>
        {isMobile && !mobileNav && (
          <div
            onClick={onToggle}
            className="nav-toggle-btn nav-toggle-btn-mobile"
          >
            <RightOutlined className="nav-icon" />
          </div>
        )}

        {!isMobile && (
          <div
            onClick={onToggle}
            className={`nav-toggle-btn`}
            style={{
              left:
                isNavSide && !navCollapsed
                  ? `${SIDE_NAV_WIDTH - 15}px`
                  : isNavSide && navCollapsed
                  ? `${SIDE_NAV_COLLAPSED_WIDTH - 15}px`
                  : "15px",
            }}
          >
            {navCollapsed ? (
              <RightOutlined className="nav-icon" />
            ) : (
              <LeftOutlined className="nav-icon" />
            )}
          </div>
        )}

        {isNavTop && !isMobile ? <TopNav routeInfo={currentRouteInfo} /> : null}
        <Layout className="app-container">
          {isNavSide && !isMobile ? (
            <SideNav
              localization={false}
              routeInfo={currentRouteInfo}
              navigationConfig={navigationConfig}
            />
          ) : null}
          <Layout
            className="app-layout"
            style={{ paddingLeft: getLayoutGutter() }}
          >
            <div
              className={`app app-content ${isNavTop ? "layout-top-nav" : ""}`}
            >
              <PageHeader
                display={currentRouteInfo?.breadcrumb}
                title={currentRouteInfo?.title}
              />
              <Layout.Content>
                <AgencyViews />
              </Layout.Content>
            </div>
          </Layout>
        </Layout>
        {isMobile && <MobileNav navigationConfig={navigationConfig} />}
      </Layout>
    </Suspense>
  );
};

const mapStateToProps = ({ theme }) => {
  const { navCollapsed, navType, locale, mobileNav } = theme;
  return { navCollapsed, navType, locale, mobileNav };
};

export default connect(mapStateToProps, {
  toggleCollapsedNav,
  onMobileNavToggle,
})(React.memo(AgencyLayout));
